import { useMemo, useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronDown, ChevronRight } from 'lucide-react'
import * as Icons from 'lucide-react'
import { cn } from '@/lib/utils'
import { MenuItem, Role, filterMenuByRole } from '@/config/dashboard-menu'
import { useWorkspaces } from '@/hooks/useWorkspaces'
import { useOrganizations } from '@/hooks/useOrganizations'
import { useSidebar } from '@/hooks/useSidebar'
import { backendApi } from '@/lib/api/backend'

interface SidebarProps {
  workspaceMenu: MenuItem[]
  className?: string
}

export function Sidebar({ workspaceMenu, className }: SidebarProps) {
  const { currentWorkspace } = useWorkspaces()
  const { currentOrg } = useOrganizations()
  const { pathname } = useLocation()
  const { isOpen } = useSidebar()

  // Phase 1 RBAC §1.3: role-gated sidebar. `currentOrg.role` is populated
  // by GET /v1/organizations (it returns OrganizationWithRole). When the
  // role isn't yet resolved the unfiltered menu is shown; backend 403s
  // remain the security boundary on direct URL navigation.
  const role = currentOrg?.role as Role | undefined
  const visibleMenu = useMemo(
    () => filterMenuByRole(workspaceMenu, role),
    [workspaceMenu, role],
  )

  const [isSuperAdmin, setIsSuperAdmin] = useState(false)
  useEffect(() => {
    backendApi.rbac.getContext()
      .then((ctx) => setIsSuperAdmin(ctx?.isSuperAdmin === true))
      .catch(() => {})
  }, [])

  return (
    <aside
      data-testid="sidebar"
      className={cn(
        'flex flex-col h-full border-r border-gray-200 dark:border-[#1a1a2e] bg-white dark:bg-[#00000f] transition-all duration-300 ease-in-out',
        // Desktop: Toggle between icon-only (w-16) and full width (w-64)
        // Mobile: Fixed overlay when open, hidden when closed
        isOpen ? 'w-64' : 'w-16',
        'md:relative md:translate-x-0',
        // Mobile overlay positioning
        !isOpen && 'max-md:-translate-x-full',
        className
      )}
    >
      {/* Main Menu */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-3">
        <div className="space-y-1">
          {visibleMenu.map((item) => (
            <div key={item.id} data-testid={`sidebar-${item.id}`}>
              <SidebarItem
                item={item}
                pathname={pathname}
                currentWorkspace={currentWorkspace}
                currentOrg={currentOrg}
                isCollapsed={!isOpen}
              />
            </div>
          ))}
        </div>
      </div>

      {isSuperAdmin && (
        <div className="border-t border-gray-200 dark:border-[#1a1a2e] py-3 px-3" data-testid="sidebar-platform-console">
          <Link
            to="/admin"
            className={cn(
              'flex items-center gap-2 px-2 py-2 text-sm rounded-md transition-colors',
              'text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950',
            )}
          >
            <Icons.Shield className="h-4 w-4" />
            {isOpen && <span>Platform Console</span>}
          </Link>
        </div>
      )}
    </aside>
  )
}

interface SidebarItemProps {
  item: MenuItem
  pathname: string
  currentWorkspace: { id: string; name: string } | null
  currentOrg: { id: string; name: string } | null
  level?: number
  isCollapsed?: boolean
}

function SidebarItem({
  item,
  pathname,
  currentWorkspace,
  currentOrg,
  level = 0,
  isCollapsed = false,
}: SidebarItemProps) {
  const { isMenuExpanded, toggleMenu } = useSidebar()
  const isItemOpen = isMenuExpanded(item.id)
  const hasChildren = item.items && item.items.length > 0

  // Get the icon component
  const IconComponent = item.icon
    ? (Icons as unknown as Record<string, typeof Icons.Circle>)[item.icon] || Icons.Circle
    : null

  // Build the actual route by replacing placeholders
  const actualRoute = item.route
    ?.replace('[id]', currentWorkspace?.id || '')
    ?.replace('[orgId]', currentOrg?.id || '')

  const isActive = actualRoute && pathname === actualRoute

  // Determine disabled state based on route pattern
  // Organization items (containing [orgId]) are disabled when no org is selected
  // Workspace items (containing [id]) are disabled when no workspace is selected
  const isOrgItem = item.route?.includes('[orgId]') || item.id === 'organization'
  const isWorkspaceItem = item.route?.includes('[id]') && !isOrgItem
  const disabled = isOrgItem ? !currentOrg : isWorkspaceItem ? !currentWorkspace : false

  // When collapsed, hide nested items
  if (isCollapsed && level > 0) {
    return null
  }

  if (hasChildren) {
    // In collapsed mode, just show as a regular link (no expansion)
    if (isCollapsed) {
      return (
        <div className="group relative">
          <div
            className={cn(
              'flex items-center justify-center w-full px-2 py-2 text-sm rounded-md transition-colors',
              'hover:bg-gray-100 dark:hover:bg-zinc-900',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            {IconComponent && <IconComponent className="h-4 w-4" />}
          </div>
          {/* Tooltip on hover */}
          <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 top-1/2 -translate-y-1/2">
            {item.label}
          </div>
        </div>
      )
    }

    // Top-level group (level 0 with children) gets special styling
    const isTopLevelGroup = level === 0

    return (
      <div>
        <button
          onClick={() => toggleMenu(item.id)}
          disabled={disabled}
          className={cn(
            'flex items-center justify-between w-full px-2 py-2 text-sm rounded-md transition-colors',
            'hover:bg-gray-100 dark:hover:bg-zinc-900',
            disabled && 'opacity-50 cursor-not-allowed',
            level > 0 && 'ml-4'
          )}
        >
          <div className="flex items-center gap-2 min-w-0">
            {IconComponent && <IconComponent className="h-4 w-4 shrink-0" />}
            {isTopLevelGroup ? (
              <span className="font-bold text-xs tracking-wide text-gray-500 dark:text-gray-400 truncate">
                {item.label.toUpperCase()}
              </span>
            ) : (
              <span className="font-medium truncate">{item.label}</span>
            )}
          </div>
          {isItemOpen ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>

        {isItemOpen && item.items && (
          <div className="mt-1 space-y-1">
            {item.items.map((child) => (
              <div key={child.id} data-testid={`sidebar-${child.id}`}>
              <SidebarItem
                item={child}
                pathname={pathname}
                currentWorkspace={currentWorkspace}
                currentOrg={currentOrg}
                level={level + 1}
                isCollapsed={isCollapsed}
              />
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  if (!item.route) {
    if (isCollapsed) {
      return (
        <div className="group relative">
          <div
            className={cn(
              'flex items-center justify-center px-2 py-2 text-sm rounded-md',
              disabled && 'opacity-50'
            )}
          >
            {IconComponent && <IconComponent className="h-4 w-4" />}
          </div>
          {/* Tooltip on hover */}
          <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 top-1/2 -translate-y-1/2">
            {item.label}
          </div>
        </div>
      )
    }

    return (
      <div
        className={cn(
          'flex items-center gap-2 px-2 py-2 text-sm rounded-md min-w-0',
          disabled && 'opacity-50',
          level > 0 && 'ml-4'
        )}
      >
        {IconComponent && <IconComponent className="h-4 w-4 shrink-0" />}
        <span className="truncate">{item.label}</span>
      </div>
    )
  }

  if (isCollapsed) {
    return (
      <div className="group relative">
        <Link
          to={disabled ? '#' : actualRoute || '#'}
          onClick={(e) => {
            if (disabled) {
              e.preventDefault()
            }
          }}
          className={cn(
            'flex items-center justify-center px-2 py-2 text-sm rounded-md transition-colors',
            'hover:bg-gray-100 dark:hover:bg-zinc-900',
            isActive && 'bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400',
            disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          {IconComponent && <IconComponent className="h-4 w-4" />}
        </Link>
        {/* Tooltip on hover */}
        <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 top-1/2 -translate-y-1/2">
          {item.label}
        </div>
      </div>
    )
  }

  return (
    <Link
      to={disabled ? '#' : actualRoute || '#'}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault()
        }
      }}
      className={cn(
        'flex items-center gap-2 px-2 py-2 text-sm rounded-md transition-colors min-w-0',
        'hover:bg-gray-100 dark:hover:bg-zinc-900',
        isActive && 'bg-blue-50 dark:bg-blue-950 text-blue-600 dark:text-blue-400 font-medium',
        disabled && 'opacity-50 cursor-not-allowed',
        level > 0 && 'ml-6'
      )}
    >
      {IconComponent && <IconComponent className="h-4 w-4 shrink-0" />}
      <span className="truncate">{item.label}</span>
    </Link>
  )
}
